import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Alert
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { usePlayer } from '../../context/PlayerContext';
import './AddCashOutDialog.scss';

const AddCashOutDialog = ({ open, onClose, playerId }) => {
  const { addCashOut, getPlayerById } = usePlayer();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  
  const player = getPlayerById(playerId);
  const MIN_CASHOUT = 0;
  const MAX_CASHOUT = 10000;

  const validateAmount = (value) => {
    const cashOutAmount = parseFloat(value);
    
    if (value === '' || isNaN(cashOutAmount) || cashOutAmount < 0) {
      return 'Please enter a valid amount (0 or greater)';
    }

    if (cashOutAmount > MAX_CASHOUT) {
      return `Maximum cash out amount is $${MAX_CASHOUT}`;
    }

    return '';
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const validationError = validateAmount(amount);
    if (validationError) {
      setError(validationError);
      return;
    }

    const cashOutAmount = parseFloat(amount);
    addCashOut(playerId, cashOutAmount, note.trim());
    setAmount('');
    setNote('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setAmount('');
    setNote('');
    setError('');
    onClose();
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setAmount(value);
      
      // Clear error when user starts typing
      if (error) {
        setError('');
      }
      
      // Show real-time validation for better UX
      if (value !== '' && !isNaN(parseFloat(value))) {
        const validationError = validateAmount(value);
        if (validationError) {
          setError(validationError);
        }
      }
    }
  };

  const isValidAmount = () => {
    if (amount === '') return false;
    const cashOutAmount = parseFloat(amount);
    return !isNaN(cashOutAmount) && cashOutAmount >= MIN_CASHOUT && cashOutAmount <= MAX_CASHOUT;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="add-cashout-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            <AccountBalanceWalletIcon className="title-icon" />
            <Box>
              <Typography variant="h5">
                Cash Out
              </Typography>
              {player && (
                <Typography variant="body2" color="text.secondary">
                  for {player.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="form-fields">
            <Alert severity="info" className="validation-info">
              Cash out amount can be $0 to ${MAX_CASHOUT}. Enter $0 if the player lost all chips.
            </Alert>
            
            {player && (
              <Alert severity="success" className="player-stats">
                Total Buy-ins: {formatCurrency(player.totalBuyIn)} | 
                Current Cash Outs: {formatCurrency(player.totalCashOut || 0)}
              </Alert>
            )}
            
            <TextField
              autoFocus
              fullWidth
              label="Cash Out Amount"
              variant="outlined"
              value={amount}
              onChange={handleAmountChange}
              error={!!error}
              helperText={error || `Enter amount from $0 to $${MAX_CASHOUT}. Use $0 if all chips were lost.`}
              placeholder="500.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    $
                  </InputAdornment>
                ),
              }}
              className="amount-input"
            />
            
            <TextField
              fullWidth
              label="Note (Optional)"
              variant="outlined"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this cash out..."
              multiline
              rows={2}
              className="note-input"
            />
          </Box>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={!isValidAmount()}
            color="success"
          >
            Cash Out
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddCashOutDialog;